/**
 * Home's "Download offline Osprey" (admins): the offline copy (lib/offline.js) with one event's TBA
 * answers built in, so the team list and schedule autofill with no internet. lib/tba.js reads them
 * back (window.__OSPREY_TBA__) whenever a TBA request fails, and starts on that event
 * (window.__OSPREY_EVENT__).
 */
import { OFFLINE_FILE_URL } from './offline';
import { tbaEventSnapshot } from './tba';

/** The offline HTML with `snapshot` (TBA path → answer) and the event built in, before any app script. */
export function withEmbeddedEvent(html, eventKey, snapshot) {
    // JSON in a <script>: escape "<" so no value can close the tag
    const json = (value) => JSON.stringify(value).replace(/</g, '\\u003c');
    const script = `<script>window.__OSPREY_EVENT__=${json(eventKey)};window.__OSPREY_TBA__=${json(snapshot)}</script>`;
    if (!html.includes('</head>')) throw new Error('offline copy has no </head>');
    return html.replace('</head>', `${script}</head>`);
}

/** True for the real offline copy - not the app's own page, which a missing file is answered with. */
export function isOfflineCopy(html) {
    return String(html).includes('window.__OSPREY_OFFLINE__=true');
}

/**
 * Download the offline copy, with `eventKey`'s TBA answers built in when one is given.
 * @returns {Promise<{ event: string|null, endpoints: number }>} how many TBA answers it carries
 */
export async function downloadOfflineCopy(eventKey) {
    const event = String(eventKey || '').trim().toLowerCase() || null;
    const res = await fetch(OFFLINE_FILE_URL, { cache: 'no-store' });
    const page = res.ok ? await res.text() : '';
    // A site without the file (the dev server, or a deploy from before it existed) answers with
    // the app's own page, which is blank from disk - never save that.
    if (!isOfflineCopy(page)) {
        throw new Error(process.env.NODE_ENV === 'development'
            ? "no offline copy built yet. Run `pnpm --filter osprey run build` once, then try again."
            : "this site has no offline copy yet. It's made by `npm run build` - deploy again.");
    }
    const snapshot = event ? await tbaEventSnapshot(event) : {};
    const html = event ? withEmbeddedEvent(page, event, snapshot) : page;
    const url = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = event ? `osprey-offline-${event}.html` : 'osprey-offline.html';
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10000);
    return { event, endpoints: Object.keys(snapshot).length };
}
